"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.dashboardTable = void 0;
const date_fns_1 = require("date-fns");
const helpers_1 = require("../../helpers");
const attributionMetrics_1 = require("../attributionMetrics");
const conversionMetrics_1 = require("../conversionMetrics");
/**
 * Example:
 * utmSourceTable(
 *  [2022-01-01, 2022-02-02],
 *  'Attribution.utmSource',
 *  'UTM Source',
 *  [{
 *    "member": "Attrubution.utmSource",
 *    "operator": "equals",
 *    "values": [
 *       "a",
 *       "b"
 *     ]
 *  }],
 *  ['Clicks'],
 * )
 **/
const dashboardTable = (dateRange, dimension, dimensionName, filters = [], conversionsIds, limit = 50000, offset = 0, disablePreaggregation = false) => ({
    mutexKey: `dashboardTable:dateRange:${dateRange[0]}:${dateRange[1]}:conversionsIds:${conversionsIds === null || conversionsIds === void 0 ? void 0 : conversionsIds.join(':')}:dimension${dimension}:filters${JSON.stringify(filters)}`,
    notPreaggregatedMeasures: [
        'Attribution.notPreaggregatedMeasure',
        ...(conversionsIds || []).map(() => 'Conversions.notPreaggregatedMeasure'),
    ],
    queries: [
        {
            measures: [
                'Attribution.cost',
                'Attribution.clicks',
                'Attribution.impressions',
                'Attribution.visitors',
                'Attribution.visits',
                ...(disablePreaggregation || new Date(dateRange[0]) < (0, date_fns_1.subDays)(new Date(), 92)
                    ? ['Attribution.notPreaggregatedMeasure']
                    : []),
            ],
            dimensions: dimension ? [dimension] : [],
            order: [['Attribution.clicks', 'desc']],
            filters: [
                ...(filters ? (0, helpers_1.filterFilters)(filters, 'Attribution.') : []),
                {
                    member: 'Attribution.date',
                    operator: 'inDateRange',
                    values: [dateRange[0], dateRange[1]],
                },
            ],
            limit,
            offset,
        },
        ...(conversionsIds || []).map(conversionId => ({
            measures: [
                'Conversions.count',
                'Conversions.conversionValue',
                'Conversions.conversionCredit',
                ...(disablePreaggregation || new Date(dateRange[0]) < (0, date_fns_1.subDays)(new Date(), 92)
                    ? ['Conversions.notPreaggregatedMeasure']
                    : []),
            ],
            dimensions: dimension ? [dimension] : [],
            filters: [
                ...(filters || []),
                {
                    member: 'Conversions.internalId',
                    operator: 'equals',
                    values: [conversionId],
                },
                {
                    member: 'Conversions.positiveConversionCredit',
                    operator: 'equals',
                    values: ['true'],
                },
                {
                    member: 'Attribution.date',
                    operator: 'inDateRange',
                    values: [dateRange[0], dateRange[1]],
                },
            ],
            limit,
            offset,
        })),
    ],
    resultProcessor: (rows) => {
        const conversionsResult = rows.slice(1).map((res, i) => ({
            conversionId: (conversionsIds === null || conversionsIds === void 0 ? void 0 : conversionsIds[i]) || '',
            rows: res.tablePivot(),
        }));
        const conversionsInfoMap = new Map();
        conversionsResult.forEach(conversionResult => {
            conversionResult.rows.forEach(row => {
                const [conversionsDimension, positiveConversionCreditCount, conversionValue, conversionCredit,] = Object.values(dimension
                    ? row
                    : Object.assign({ dimension: '' }, row));
                const conversionsByDimensions = conversionsInfoMap.get(conversionsDimension) || new Map();
                conversionsByDimensions.set(conversionResult.conversionId, {
                    positiveConversionCreditCount,
                    conversionValue,
                    conversionCredit,
                });
                conversionsInfoMap.set(conversionsDimension, conversionsByDimensions);
            });
        });
        const attributions = rows[0].tablePivot().map(row => {
            const [attributionDimension, cost, clicks, impressions, visitors, visits] = Object.values(dimension
                ? row
                : Object.assign({ dimension: '' }, row));
            const conversionsByDimension = conversionsInfoMap.get(attributionDimension);
            const conversionsInfo = {};
            for (const conversionId of conversionsIds || []) {
                const conversionInfo = conversionsByDimension === null || conversionsByDimension === void 0 ? void 0 : conversionsByDimension.get(conversionId);
                conversionsInfo[conversionId] = conversionInfo || {
                    positiveConversionCreditCount: 0,
                    conversionValue: 0,
                    conversionCredit: 0,
                };
            }
            const attribution = {
                cost,
                clicks,
                impressions,
                visitors,
                visits,
            };
            const attributionMetricsResult = (0, attributionMetrics_1.calculateAttributionMetrics)(attribution);
            return Object.assign(Object.assign({ dimension: attributionDimension }, Object.keys(attributionMetrics_1.attributionMetrics).reduce((res, key) => {
                res[attributionMetrics_1.attributionMetrics[key]] = attributionMetricsResult[key];
                return res;
            }, {})), Object.entries(conversionsInfo).reduce((res, [conversionId, conversionInfo]) => {
                const calculatedConversionMetrics = (0, conversionMetrics_1.calculateConversionMetrics)(attribution, conversionInfo);
                Object.keys(conversionMetrics_1.conversionMetrics).forEach(key => {
                    res[`${conversionId}_${conversionMetrics_1.conversionMetrics[key]}`] = calculatedConversionMetrics[key];
                });
                return res;
            }, {}));
        });
        return {
            lastRefreshTime: new Date(Math.max(...rows.flatMap(row => row
                .serialize()
                .loadResponse.results.map(result => new Date(result.lastRefreshTime).valueOf())))),
            tableColumns: () => [
                {
                    key: 'dimension',
                    title: dimensionName || '',
                    shortTitle: 'dimension',
                    type: 'string',
                    dataIndex: 'dimension',
                    meta: '',
                },
                ...Object.values(attributionMetrics_1.attributionMetrics).map(key => ({
                    key: key,
                    title: key,
                    shortTitle: key,
                    type: 'number',
                    dataIndex: key,
                    meta: {
                        metric: key,
                    },
                })),
                ...(conversionsIds || []).flatMap(conversionId => Object.values(conversionMetrics_1.conversionMetrics).map(metric => ({
                    key: `${conversionId}_${metric}`,
                    title: metric,
                    shortTitle: metric,
                    type: 'number',
                    dataIndex: `${conversionId}_${metric}`,
                    meta: {
                        conversionId,
                        metric,
                    },
                }))),
            ],
            tablePivot: () => attributions,
        };
    },
});
exports.dashboardTable = dashboardTable;
//# sourceMappingURL=table.js.map